import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_URL } from "@/lib/data";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass-strong" : ""}`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Lucra Photography"
            className="h-10 w-10 object-contain"
            style={{ filter: "drop-shadow(0 0 12px rgba(212,175,55,0.25))" }}
          />
          <div className="leading-none">
            <div className="font-display text-xl tracking-tight text-white">LUCRA</div>
            <div className="font-mono-tech text-[10px] text-white/40 mt-0.5">PHOTOGRAPHY · ATL / DC</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 font-mono-tech text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  isActive ? "text-[var(--gold)]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[var(--gold)]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="nav-cta" className="btn-gold">
            <span>Book a Session</span>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        } glass-strong`}
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `py-3 font-display text-2xl tracking-tight ${isActive ? "text-[var(--gold)]" : "text-white/85"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" className="btn-gold mt-4 w-fit" data-testid="mobile-nav-cta">
            <span>Book a Session</span>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
